// Persist an in-progress build interview to sessionStorage so it survives a full page
// navigation / reload (e.g. hitting the AI cap → going to Billing to upgrade → coming back).
// Without this the interview lives only in a useRef + an in-memory chat log, so navigating away
// wipes every answer. Cleared when the interview completes or is cancelled.
//
// Scoped by ACCOUNT as well as site, in the key and in the payload. The old single global key
// relied on the account-switch reset to wipe it — and a site id is not an account: an admin in
// Test Mode and the agent they are impersonating can have the same site on screen. Review on #750.
import type { GuidedAnswers, StepId } from './guidedInterview';

/** The pre-scoping key. Its owner is unknown, so it is never loaded — only removed. */
export const LEGACY_INTERVIEW_KEY = 'bls.buildInterview.v1';
const KEY_PREFIX = 'bls.buildInterview.v2.';

interface Persisted {
  owner: string;
  siteId: string;
  answers: GuidedAnswers;
  skipped: StepId[];
  savedAt: number;
}

/** The account an interview belongs to: tenant AND user, like the rest of the account state. */
export function interviewOwner(
  tenantId: string | null | undefined,
  userId: string | null | undefined,
): string | null {
  return tenantId && userId ? `${tenantId}:${userId}` : null;
}

function keyFor(owner: string): string {
  return `${KEY_PREFIX}${encodeURIComponent(owner)}`;
}

function dropLegacy(): void {
  try { sessionStorage.removeItem(LEGACY_INTERVIEW_KEY); } catch { /* ignore */ }
}

/** Save the interview for `owner` on `siteId`. The base64 logo set is stripped (large +
 *  re-generatable) so we stay well under the storage quota; on resume the logo step is simply
 *  re-offered. Best-effort. `owner` is the account the interview was STARTED in — never "whoever is
 *  signed in now", or a save that runs after an account switch would file one account's answers
 *  under the other's name. */
export function saveInterview(
  owner: string | null | undefined,
  siteId: string | null | undefined,
  answers: GuidedAnswers,
  skipped: Set<StepId>,
  now: number,
): void {
  if (!owner || !siteId) return;
  try {
    const { logo: _logo, ...light } = answers; // drop the heavy generated-logo base64
    const payload: Persisted = { owner, siteId, answers: light, skipped: [...skipped], savedAt: now };
    sessionStorage.setItem(keyFor(owner), JSON.stringify(payload));
  } catch {
    /* quota exceeded / storage unavailable — persistence is best-effort */
  }
}

/** Load a saved interview iff it belongs to this account AND this site. */
export function loadInterview(
  owner: string | null | undefined,
  siteId: string | null | undefined,
): { answers: GuidedAnswers; skipped: Set<StepId> } | null {
  // Whatever the old global key holds could be anyone's: it is dropped, never adopted.
  dropLegacy();
  if (!owner || !siteId) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(owner));
    if (!raw) return null;
    const p = JSON.parse(raw) as Persisted;
    if (!p || p.owner !== owner || p.siteId !== siteId) return null;
    return { answers: p.answers ?? {}, skipped: new Set(p.skipped ?? []) };
  } catch {
    return null;
  }
}

/** Drop `owner`'s saved interview (completed, cancelled, or nothing left to ask). */
export function clearInterview(owner: string | null | undefined): void {
  dropLegacy();
  if (!owner) return;
  try {
    sessionStorage.removeItem(keyFor(owner));
  } catch {
    /* ignore */
  }
}

/** Drop every account's saved interview in this tab — on sign-out. */
export function clearAllInterviews(): void {
  dropLegacy();
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(KEY_PREFIX)) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}
